import { NgTemplateOutlet, SlicePipe } from '@angular/common';
import type {
  AfterContentInit,
  AfterViewInit,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  TemplateRef,
  TrackByFunction,
  inject,
} from '@angular/core';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { CalloutComponent, CalloutPlacement } from '@freelancer/ui/callout';
import { IconModule, IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { PaginationComponent } from '@freelancer/ui/pagination';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { TableColumnComponent } from './table-column.component';
import { TableExpandableContentComponent } from './table-expandable-content.component';
import type { TableBorderRadius, TableRow } from './table.types';
import {
  HeaderWidth,
  TableColumnPaddingSize,
  TableHeaderColor,
  TableMobileStyle,
  TablePaginationPosition,
} from './table.types';
import { CalloutContentComponent } from '../callout/callout-content.component';
import { CalloutTriggerComponent } from '../callout/callout-trigger.component';
import { LinkModule } from '../link/link.module';
import { LinkOutline } from '../link/link.types';

@Component({
  standalone: true,
  selector: 'fl-table',
  imports: [
    CalloutComponent,
    CalloutContentComponent,
    CalloutTriggerComponent,
    FreelancerAsyncPipeModule,
    IconModule,
    LinkModule,
    NgTemplateOutlet,
    PaginationComponent,
    SlicePipe,
  ],
  template: `
    <table
      class="Table"
      [attr.data-fixed-layout]="fixedLayout"
      [attr.data-row-links]="rowLinks"
    >
      @if (!hideHeader) {
        <thead
          class="HeaderGroup"
          [class.mobile:hide]="responsive"
        >
          <tr
            class="Row HeaderRow"
            [attr.data-border]="border"
            [attr.data-header-color]="headerColor"
            [attr.data-expandable-body-compact]="expandableBodyCompact"
            [attr.data-header-separator]="headerSeparator"
            [attr.data-edge-to-edge]="edgeToEdge"
            [attr.data-padding-size]="paddingSize"
            [attr.data-padding-size-tablet]="paddingSizeTablet"
            [attr.data-padding-size-desktop]="paddingSizeDesktop"
          >
            @for (
              column of columns$ | flAsync;
              track trackByColumn($index, column)
            ) {
              <th
                class="Cell HeaderCell"
                [attr.data-edge-to-edge]="edgeToEdge"
                [attr.data-row-links]="rowLinks"
                [attr.data-text-align]="column.textAlign"
                [attr.data-nowrap]="column.sortable || column.headerNoWrap"
                [attr.data-padding-size]="paddingSize"
                [attr.data-padding-size-tablet]="paddingSizeTablet"
                [attr.data-padding-size-desktop]="paddingSizeDesktop"
                [attr.data-grid-width]="column.gridWidth"
                [class.hide]="column.hide"
                [class.mobile:hide]="column.hideMobile"
                [class.tablet:hide]="column.hideTablet"
                [class.desktop:hide]="column.hideDesktop"
              >
                <div [class]="headerTextClasses">
                  @if (column.headerTemplate) {
                    <ng-container
                      [ngTemplateOutlet]="column.headerTemplate"
                    ></ng-container>
                  } @else {
                    @if (!column.sortable) {
                      {{ column.titleText }}
                    }
                    @if (column.sortable) {
                      <fl-link
                        [color]="LinkColor.INHERIT"
                        (click)="column.sortClicked.emit()"
                      >
                        {{ column.titleText }}
                        <fl-icon
                          [name]="'ui-unfold'"
                          [size]="IconSize.SMALL"
                          [color]="IconColor.INHERIT"
                          [useIconFont]="true"
                        ></fl-icon>
                      </fl-link>
                    }
                    @if (column.tooltipIconName) {
                      <fl-callout
                        [edgeToEdge]="true"
                        [hover]="true"
                        [placement]="CalloutPlacement.BOTTOM"
                        [size]="column.calloutSize"
                      >
                        <fl-callout-trigger>
                          <fl-icon
                            [name]="column.tooltipIconName"
                            [size]="IconSize.SMALL"
                            [useIconFont]="true"
                            [fillIconFont]="column.fillIcon"
                          ></fl-icon>
                        </fl-callout-trigger>
                        <fl-callout-content>
                          <ng-container
                            [ngTemplateOutlet]="column.calloutContent"
                          ></ng-container>
                        </fl-callout-content>
                      </fl-callout>
                    }
                  }
                </div>
              </th>
            }
            <!-- Custom expandables are declared in the parent, so omit this header row -->
            @if (expandable && !customExpandable) {
              <th [class.HeaderColumnExpandable]="expandableBodyCompact"></th>
            }
          </tr>
        </thead>
      }
      <tbody
        class="BodyGroup"
        [attr.data-header-separator]="headerSeparator"
        [attr.data-footer-separator]="footerSeparator"
      >
        @for (
          row of tableRows
            | slice
              : (currentPage - 1) * itemsPerPage
              : currentPage * itemsPerPage;
          track rowTrackByLocal(i, row);
          let i = $index
        ) {
          <tr
            class="Row BodyRow"
            [attr.data-edge-to-edge]="edgeToEdge"
            [attr.data-border]="border"
            [attr.data-row-links]="rowLinks"
            [attr.data-hover]="hover"
            [attr.data-highlight]="row.highlight"
            [attr.data-flash-highlight]="row.flashHighlight"
            [attr.data-highlight-border]="row.highlightBorder?.color"
            [attr.data-highlight-border-mobile-only]="
              row.highlightBorder?.mobileOnly
            "
            [attr.data-expandable]="expandable"
            [attr.data-mobile-view-compact]="mobileViewCompact"
            [attr.data-expandable-body-compact]="expandableBodyCompact"
            [attr.data-mobile-view-shading]="mobileViewShading"
            [attr.data-row]="i"
          >
            @for (
              column of columns$ | flAsync;
              track trackByColumn($index, column);
              let first = $first
            ) {
              <td
                class="Cell BodyCell"
                [attr.data-edge-to-edge]="edgeToEdge"
                [attr.data-vertical-align]="column.verticalAlign"
                [attr.data-row-links]="rowLinks"
                [attr.data-padding-size]="paddingSize"
                [attr.data-padding-size-tablet]="paddingSizeTablet"
                [attr.data-padding-size-desktop]="paddingSizeDesktop"
                [attr.data-text-align]="column.textAlign"
                [attr.data-grid-width]="column.gridWidth"
                [attr.data-column-placement]="column.columnPlacement"
                [class.mb-xxsmall]="
                  !(rowLinks || paddingSize === TableColumnPaddingSize.NONE)
                "
                [class.tablet:mb-xxsmall]="
                  !(
                    rowLinks ||
                    paddingSizeTablet === TableColumnPaddingSize.NONE
                  )
                "
                [class.desktop:mb-xxsmall]="
                  !(
                    rowLinks ||
                    paddingSizeDesktop === TableColumnPaddingSize.NONE
                  )
                "
                [class.hide]="column.hide"
                [class.mobile:hide]="column.hideMobile"
                [class.tablet:hide]="column.hideTablet"
                [class.desktop:hide]="column.hideDesktop"
              >
                <!--
                we hide this text with ngIf instead of adding it with CSS :before content
                this lets us use the fl-text styling here
                FIXME: T273892 - Currently rowLinks is not supported with mobileViewCompact = true or expandable = true
                -->
                @if (!mobileViewCompact) {
                  @if (rowLinks) {
                    <fl-link
                      class="Cell LinkCell"
                      flTrackingLabel="TableRowLink"
                      [attr.data-border]="border"
                      [attr.data-row-links]="rowLinks"
                      [attr.data-padding-size]="paddingSize"
                      [attr.data-padding-size-tablet]="paddingSizeTablet"
                      [attr.data-padding-size-desktop]="paddingSizeDesktop"
                      [attr.data-edge-to-edge]="edgeToEdge"
                      [attr.data-text-align]="column.textAlign"
                      [attr.data-vertical-align]="column.verticalAlign"
                      [color]="LinkColor.INHERIT"
                      [link]="row?.link"
                      [newTab]="row?.newTab"
                      [outline]="LinkOutline.INHERIT"
                      [tabIndex]="first ? 0 : -1"
                      [underline]="LinkUnderline.NEVER"
                    >
                      <ng-container
                        *ngTemplateOutlet="cellContent"
                      ></ng-container>
                    </fl-link>
                  } @else {
                    <ng-container
                      [ngTemplateOutlet]="cellContent"
                    ></ng-container>
                  }
                }
                <ng-template #cellContent>
                  <div
                    class="ContentWrapper"
                    [attr.data-row-links]="rowLinks"
                  >
                    @if (responsive) {
                      <div
                        class="MobileHeader font-bold text-foreground text-body-16 mr-xxsmall mobile:show"
                        [attr.data-header-width]="headerWidth"
                      >
                        {{ column.titleText }}
                        @if (column.tooltipIconName) {
                          <fl-callout
                            [edgeToEdge]="true"
                            [hover]="true"
                            [placement]="CalloutPlacement.BOTTOM"
                            [size]="column.calloutSize"
                          >
                            <fl-callout-trigger>
                              <fl-icon
                                [name]="column.tooltipIconName"
                                [size]="IconSize.SMALL"
                                [useIconFont]="true"
                                [fillIconFont]="column.fillIcon"
                              ></fl-icon>
                            </fl-callout-trigger>
                            <fl-callout-content>
                              <ng-container
                                [ngTemplateOutlet]="column.calloutContent"
                              ></ng-container>
                            </fl-callout-content>
                          </fl-callout>
                        }
                      </div>
                    }
                    <p class="CellText">
                      <ng-container
                        [ngTemplateOutlet]="column.cellTemplate"
                        [ngTemplateOutletContext]="{
                          $implicit: row.item,
                          index: i,
                        }"
                      ></ng-container>
                    </p>
                  </div>
                </ng-template>
                @if (mobileViewCompact) {
                  <div class="ContentWrapper">
                    <div class="CellText">
                      <ng-container
                        [ngTemplateOutlet]="column.cellTemplate"
                        [ngTemplateOutletContext]="{
                          $implicit: row.item,
                          index: i,
                        }"
                      ></ng-container>
                    </div>
                  </div>
                }
              </td>
            }
            @if (expandable && !customExpandable) {
              <td
                class="Cell BodyCell CellIcon"
                [attr.data-edge-to-edge]="edgeToEdge"
                [attr.data-row-links]="rowLinks"
                [attr.data-padding-size]="paddingSize"
                [attr.data-padding-size-tablet]="paddingSizeTablet"
                [attr.data-padding-size-desktop]="paddingSizeDesktop"
                [class.desktop:show]="!expandableMobileEnabled"
              >
                <fl-icon
                  class="ExpandIcon"
                  label="Expand"
                  i18n-label="Label for icon to expand table row"
                  [name]="'ui-chevron-down'"
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                  [weight]="FontWeight.MEDIUM"
                  (click)="toggleExpandedContent(i)"
                ></fl-icon>
              </td>
            }
          </tr>
          @if (expandable && expandableContent) {
            <tr
              class="Row BodyRow IsHidden BodyRowExpandable"
              [attr.data-edge-to-edge]="
                expandableContent.edgeToEdge || edgeToEdge
              "
              [attr.data-border]="border"
              [attr.data-row-links]="rowLinks"
              [attr.data-hover]="hover"
              [attr.data-highlight]="row.highlight"
              [attr.data-flash-highlight]="row.flashHighlight"
              [attr.data-highlight-border]="row.highlightBorder?.color"
              [attr.data-highlight-border-mobile-only]="
                row.highlightBorder?.mobileOnly
              "
              [attr.data-expandable]="expandable"
              [attr.data-expandable-body-compact]="expandableBodyCompact"
              [attr.data-background-color]="expandableContent.backgroundColor"
              [attr.data-expandable-row]="i"
            >
              @if (!expandableBodyCompact) {
                <td
                  [colSpan]="
                    customExpandable
                      ? (columns$ | flAsync)?.length
                      : (columns$ | flAsync)?.length + 1
                  "
                >
                  <ng-container
                    [ngTemplateOutlet]="expandableContent.contentTemplate"
                    [ngTemplateOutletContext]="{
                      $implicit: row.item,
                      index: i,
                    }"
                  ></ng-container>
                </td>
              }
              @if (mobileViewCompact && expandableBodyCompact) {
                <td class="Cell BodyCell tablet:hide desktop:hide">
                  @for (
                    column of columns$ | flAsync;
                    track trackByColumn($index, column)
                  ) {
                    <div
                      class="BodyCellExpandable mb-xxsmall"
                      [attr.data-column-placement]="column.columnPlacement"
                    >
                      <div class="ContentWrapper">
                        <p
                          class="MobileHeader font-bold mr-xxsmall mobile:show"
                          [attr.data-header-width]="headerWidth"
                        >
                          {{ column.titleText }}
                        </p>
                        <p class="CellText">
                          <ng-container
                            [ngTemplateOutlet]="column.cellTemplate"
                            [ngTemplateOutletContext]="{
                              $implicit: row.item,
                              index: i,
                            }"
                          ></ng-container>
                        </p>
                      </div>
                    </div>
                  }
                </td>
              }
            </tr>
          }
        }
      </tbody>
      @if (footerTemplates?.length) {
        <tfoot
          class="FooterGroup"
          [attr.data-footer-separator]="footerSeparator"
          [attr.data-edge-to-edge]="edgeToEdge"
          [attr.data-padding-size]="paddingSize"
          [attr.data-padding-size-tablet]="paddingSizeTablet"
          [attr.data-padding-size-desktop]="paddingSizeDesktop"
        >
          <tr class="Row FooterRow">
            @let templates = footerTemplates.toArray();
            @for (
              template of templates;
              track $index;
              let first = $first;
              let last = $last
            ) {
              <td
                class="Cell FooterCell"
                [attr.data-edge-to-edge]="edgeToEdge"
                [attr.data-padding-size]="paddingSize"
                [attr.data-padding-size-tablet]="paddingSizeTablet"
                [attr.data-padding-size-desktop]="paddingSizeDesktop"
                [attr.data-header-width]="first ? headerWidth : null"
                [colSpan]="
                  first
                    ? (columns$ | flAsync)?.length - templates.length + 1
                    : 1
                "
              >
                <ng-container
                  [ngTemplateOutlet]="template"
                  [ngTemplateOutletContext]="{ $implicit: dataSource }"
                ></ng-container>
              </td>
            }
          </tr>
        </tfoot>
      }
    </table>
    @if (dataSource.length > itemsPerPage && totalPages) {
      <fl-pagination
        class="TablePagination"
        [attr.data-pagination-position]="paginationPosition"
        [attr.data-border]="border"
        [compact]="compactPagination"
        [currentPage]="currentPage"
        [pages]="totalPages"
        (pageSelected)="onPageSelected($event)"
      ></fl-pagination>
    }
  `,
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T>
  implements AfterContentInit, AfterViewInit, OnChanges, OnInit
{
  private elementRef = inject(ElementRef);

  CalloutPlacement = CalloutPlacement;
  IconSize = IconSize;
  IconColor = IconColor;
  FontWeight = FontWeight;
  LinkColor = LinkColor;
  LinkOutline = LinkOutline;
  LinkUnderline = LinkUnderline;
  TableColumnPaddingSize = TableColumnPaddingSize;

  // FIXME: T300116 Enable hydration. Currently fails due to mismatched nodes
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  @Input() dataSource: readonly T[];
  @Input() rowTrackBy?: TrackByFunction<T>;
  @Input() border = true;
  @Input() hover = false;
  @Input() edgeToEdge = false;
  @Input() hideHeader = false;
  @Input() enableAutoExpandOnSingleItem = false;
  @Input() expandable = false;
  /** Allows showing or hiding of the expandable toggle in mobile screens. Will only have an effect when `expandable` is true */
  @Input() expandableMobileEnabled = true;
  @Input() expandableBodyCompact = false;
  @Input() headerColor = TableHeaderColor.DEFAULT;
  @Input() headerFontWeight = FontWeight.BOLD;
  @Input() headerWidth = HeaderWidth.SMALL;

  /** Allow table rows to be links. Currently not supported with `expandable`, `mobileViewCompact` and `calloutContent`. */
  @Input() rowLinks = false;

  /** Allow programmatically expanding the table without showing the chevron icon */
  @Input() customExpandable = false;
  /** Only allow expanding one row at a time */
  @Input() singleExpandable = false;
  /** Use with any width input on fl-table-col to fix table layout */
  @Input() fixedLayout = false;

  /** Set pagination component to use compact mode */
  @Input() compactPagination = false;
  @Input() itemsPerPage: number = Number.POSITIVE_INFINITY;
  /** Position applys only for tablet viewport and up */
  @Input() paginationPosition = TablePaginationPosition.CENTER;
  @Input() initialPage?: number = 1;

  @Input() paddingSize = TableColumnPaddingSize.MID;
  @Input() paddingSizeTablet?: TableColumnPaddingSize;
  @Input() paddingSizeDesktop?: TableColumnPaddingSize;

  /** The total count of items in the table. Used for when we don't retrieve all records in one go*/
  @Input() totalCount?: number;

  /** Adds a shaded color to every second row in mobile view */
  @Input() mobileViewShading = false;

  /** Hide table outer box shadow but keep the individual cell box shadow in mobile view */
  @HostBinding('attr.data-hide-box-shadow')
  @Input()
  hideBoxShadow = false;

  @HostBinding('attr.data-responsive')
  @Input()
  responsive? = true;

  @HostBinding('attr.data-mobile-view-compact')
  @Input()
  mobileViewCompact?: TableMobileStyle;

  @HostBinding('attr.data-radius')
  @Input()
  borderRadius: TableBorderRadius = 'none';

  /** Show a separator border between header and body */
  @Input() headerSeparator = false;

  /** Show a separator border between body and footer */
  @Input() footerSeparator = false;

  @Output() pageSelected = new EventEmitter<number>();
  /** Emits the index of the currently expanded row - undefined if no row is expanded */
  @Output() rowExpanded = new EventEmitter<number | undefined>();

  @ContentChild(TableExpandableContentComponent)
  expandableContent: TableExpandableContentComponent;

  @ContentChildren('footer') footerTemplates: QueryList<
    TemplateRef<{
      $implicit: readonly T[];
    }>
  >;

  @ContentChildren(TableColumnComponent<T>)
  childColumns: QueryList<TableColumnComponent<T>>;

  columns$: Observable<TableColumnComponent<T>[]>;
  tableRows: readonly TableRow<T>[];

  // if we should use the grid display instead of flat rows
  showTableGrid$: Observable<boolean>;
  currentPage = 1;
  totalPages = 5;

  // indices of rows that are currently expanded
  private currentlyExpandedRows: readonly number[] = [];

  headerTextClasses: string;

  rowTrackByLocal(index: number, tableRow: TableRow<T>): any {
    return this.rowTrackBy ? this.rowTrackBy(index, tableRow.item) : undefined;
  }

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dataSource) {
      this.tableRows = this.dataSource.map(sourceItem => ({
        item: sourceItem,
        highlight: (sourceItem as any).highlight,
        flashHighlight: (sourceItem as any).flashHighlight,
        link: (sourceItem as any).link,
        newTab: (sourceItem as any).newTab,
        highlightBorder: (sourceItem as any).highlightBorder,
      }));
      this.totalPages = this.totalCount
        ? Math.ceil(this.totalCount / this.itemsPerPage)
        : Math.ceil(this.dataSource.length / this.itemsPerPage);
    }
    if (this.initialPage && 'initialPage' in changes) {
      this.currentPage = this.initialPage;
    }
    // FIXME: T273892 - Currently rowLinks is not supported with `mobileViewCompact = true` or `expandable = true`
    if (
      (this.rowLinks && 'rowLinks' in changes) ||
      (this.mobileViewCompact && 'mobileViewCompact' in changes) ||
      (this.expandable && 'expandable' in changes)
    ) {
      this.rowLinks =
        this.rowLinks && !(this.mobileViewCompact || this.expandable);
    }

    if ('headerColor' in changes || 'headerFontWeight' in changes) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.headerTextClasses = textVariants({
      color:
        this.headerColor === TableHeaderColor.DARK
          ? FontColor.LIGHT
          : FontColor.FOREGROUND,
      weight: this.headerFontWeight,
      size: TextSize.BODY_16,
    });
  }

  ngAfterViewInit(): void {
    if (
      this.enableAutoExpandOnSingleItem &&
      this.dataSource?.length === 1 &&
      this.expandable &&
      this.expandableContent
    ) {
      this.toggleExpandedContent(0);
    }
  }

  ngAfterContentInit(): void {
    this.columns$ = this.childColumns.changes.pipe(
      startWith(this.childColumns.toArray()),
    );
  }

  /** For use with custom expandable tables */
  toggleExpandedContent(rowIndex: number): void {
    if (
      this.singleExpandable &&
      this.currentlyExpandedRows.length > 0 &&
      !this.currentlyExpandedRows.includes(rowIndex)
    ) {
      this.toggleRow(this.currentlyExpandedRows[0]); // close old row
    }

    this.toggleRow(rowIndex);
  }

  private toggleRow(rowIndex: number): void {
    const { nativeElement } = this.elementRef;

    const row: HTMLElement = nativeElement.querySelector(
      `[data-row="${rowIndex}"]`,
    ) as HTMLElement;

    const expandableRow: HTMLElement = nativeElement.querySelector(
      `[data-expandable-row="${rowIndex}"]`,
    ) as HTMLElement;

    if (row && expandableRow) {
      row.classList.toggle('IsActive');
      expandableRow.classList.toggle('IsHidden');
      expandableRow.classList.toggle('IsActive');
    }

    // if a currently expanded row is toggled, remove it from the buffer
    if (this.currentlyExpandedRows.includes(rowIndex)) {
      this.currentlyExpandedRows = this.currentlyExpandedRows.filter(
        i => rowIndex !== i,
      );
    } else {
      this.currentlyExpandedRows = [...this.currentlyExpandedRows, rowIndex];
    }

    if (this.currentlyExpandedRows.length === 0) {
      this.rowExpanded.emit(undefined);
    } else {
      this.rowExpanded.emit(rowIndex);
    }
  }

  onPageSelected(page: number): void {
    this.currentPage = page;
    this.pageSelected.emit(this.currentPage);

    // Reset expanded rows
    if (this.expandable) {
      this.currentlyExpandedRows = [];
      this.rowExpanded.emit(undefined);
    }
  }

  trackByColumn(
    _: number,
    column: TableColumnComponent<T>,
  ): TableColumnComponent<T> {
    return column;
  }
}
